/**
 * ResetPasswordForm - Reset password after OTP verification
 */
import React, { useState } from 'react';
import { message } from 'antd';
import { useAuthContext } from '../../context/AuthContext';
import { useAuth } from '../../hooks/useAuth';
import { useAuthForm } from '../../hooks/useAuthForm';
import { AUTH_MODES, INPUT_TYPES } from '../../types';
import { AuthHeader, AuthInput, AuthButton, OtpInput, ResendOtp, PasswordRequirements } from '../ui';

function ResetPasswordForm() {
  const { identifier, identifierType, loading, goBack, switchMode } = useAuthContext();
  const { resetPassword, sendForgotPasswordOtp, verifyForgotPasswordOtp } = useAuth();
  const { 
    otp,
    password, 
    confirmPassword, 
    error, 
    handleOtpChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    getPasswordValidation,
    doPasswordsMatch,
    isOtpValid,
  } = useAuthForm();
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState('otp'); // 'otp' | 'password'
  
  const passwordValidation = getPasswordValidation();
  const isPasswordFormValid = passwordValidation.isValid && doPasswordsMatch();
  
  // Mask identifier for display
  const maskedIdentifier = identifierType === INPUT_TYPES.MOBILE
    ? `******${identifier.slice(-4)}`
    : identifier.replace(/(.{3})(.*)(@.*)/, '$1***$3');
  
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (isOtpValid()) {
      const success = await verifyForgotPasswordOtp();
      if (success) {
        setStep('password');
      }
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordFormValid) return;
    
    const success = await resetPassword();
    if (success) {
      message.success('Password reset successful! Please login with your new password.');
      // After successful reset, go to login
      switchMode(AUTH_MODES.LOGIN);
    }
  };
  
  const EyeIcon = (show) => show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
  
  // OTP verification step
  if (step === 'otp') {
    return (
      <div className="relative p-6 md:p-8 bg-white">
        <AuthHeader
          title="Verify OTP"
          subtitle={`Enter the 6-digit code sent to ${maskedIdentifier}`}
          showBack
          onBack={goBack}
        />
        
        <form onSubmit={handleVerifyOtp} className="space-y-6">
          <OtpInput
            value={otp}
            onChange={handleOtpChange}
            error={error}
            disabled={loading}
            autoFocus
          />
          
          <AuthButton
            type="submit"
            loading={loading}
            disabled={!isOtpValid()}
          >
            Verify OTP
          </AuthButton>
          
          <ResendOtp onResend={sendForgotPasswordOtp} disabled={loading} />
        </form>
      </div>
    );
  }
  
  // Password reset step
  return (
    <div className="relative p-6 md:p-8 bg-white">
      <AuthHeader
        title="Reset Password"
        subtitle="Create a new secure password"
        showBack
        onBack={() => setStep('otp')}
      />
      
      <form onSubmit={handleResetPassword} className="space-y-5">
        <PasswordRequirements validation={passwordValidation} />
        
        <AuthInput
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          placeholder="Enter new password"
          disabled={loading}
          rightIcon={EyeIcon(showPassword)}
          onRightIconClick={() => setShowPassword(!showPassword)}
        />
        
        <AuthInput
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          placeholder="Re-enter password"
          error={confirmPassword && !doPasswordsMatch() ? 'Passwords do not match' : ''}
          disabled={loading}
          rightIcon={EyeIcon(showConfirmPassword)}
          onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />
        
        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}
        
        <AuthButton
          type="submit"
          loading={loading}
          disabled={!isPasswordFormValid}
        >
          Reset Password
        </AuthButton>
      </form>
    </div>
  );
}

export default ResetPasswordForm;
